import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  inject,
  Input,
} from '@angular/core';
import { ThemeService } from '@freelancer/theme';
import {
  CarouselArrowPosition,
  CarouselScrollBehaviour,
} from '@freelancer/ui/carousel';
import { ContainerSize } from '@freelancer/ui/container';
import type { PictureImage } from '@freelancer/ui/picture';
import { PictureLoadMethod, PictureObjectFit } from '@freelancer/ui/picture';
import type { FontColor } from '@freelancer/ui/text';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { SectionCompaniesTitleComponent } from './section-companies-title.component';
import {
  SectionCompanyLogo,
  SectionCompanyLogoColor,
} from './section-companies.types';

@UntilDestroy({ className: 'SectionCompaniesComponent' })
@Component({
  standalone: false,
  selector: 'fl-section-companies',
  template: `
    <fl-container
      class="SectionCompanies"
      [size]="containerSize"
    >
      <p
        class="text-body-16 desktop:text-body-20 mb-small tablet:mb-none tablet:mr-mid desktop:mr-xlarge desktop_large:mr-xxxxlarge"
        [class.text-light]="color === SectionCompanyLogoColor.LIGHT"
        [class.text-foreground]="color !== SectionCompanyLogoColor.LIGHT"
      >
        @if (sectionCompaniesTitle) {
          <ng-content select="fl-section-companies-title"></ng-content>
        } @else {
          <span i18n="As used by text">As used by</span>
        }
      </p>
      <div class="SectionCompanies-logosContainer">
        @for (logo of logoDetails; track trackBySrc($index, logo)) {
          <div class="SectionCompanies-logo mobile:hide">
            <fl-picture
              class="SectionCompanies-logo-image"
              [alt]="logo.alt"
              [boundedWidth]="true"
              [boundedHeight]="true"
              [loadMethod]="PictureLoadMethod.LAZY"
              [objectFit]="PictureObjectFit.SCALE_DOWN"
              [src]="logo.src"
            ></fl-picture>
          </div>
        }

        <fl-carousel
          class="mobile:show"
          [currentIndex]="0"
          [slidesToShow]="3"
          [padding]="4"
          [arrowPosition]="CarouselArrowPosition.OUTSIDE"
          [scrollBehaviour]="CarouselScrollBehaviour.GROUP"
        >
          <fl-carousel-prev-btn>
            <fl-icon
              [name]="'ui-arrow-back'"
              [useIconFont]="true"
            ></fl-icon>
          </fl-carousel-prev-btn>
          @for (logo of logoDetails; track trackBySrc($index, logo)) {
            <fl-carousel-item class="SectionCompanies-logo">
              <fl-picture
                class="SectionCompanies-logo-image"
                [alt]="logo.alt"
                [boundedWidth]="true"
                [boundedHeight]="true"
                [loadMethod]="PictureLoadMethod.LAZY"
                [objectFit]="PictureObjectFit.SCALE_DOWN"
                [src]="logo.src"
              ></fl-picture>
            </fl-carousel-item>
          }
          <fl-carousel-next-btn>
            <fl-icon
              [name]="'ui-arrow-forward'"
              [useIconFont]="true"
            ></fl-icon>
          </fl-carousel-next-btn>
        </fl-carousel>
      </div>
    </fl-container>
  `,
  styleUrls: ['./section-companies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCompaniesComponent implements OnInit {
  private theme = inject(ThemeService);
  private cdr = inject(ChangeDetectorRef);

  CarouselArrowPosition = CarouselArrowPosition;
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  PictureObjectFit = PictureObjectFit;
  PictureLoadMethod = PictureLoadMethod;
  SectionCompanyLogoColor = SectionCompanyLogoColor;

  @Input() logos: readonly SectionCompanyLogo[];
  @Input() color: SectionCompanyLogoColor = SectionCompanyLogoColor.DARK;
  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_XXLARGE;

  @ContentChild(SectionCompaniesTitleComponent)
  sectionCompaniesTitle: SectionCompaniesTitleComponent;

  logoDetails: readonly PictureImage[];
  acceptedPNGLogos = [
    SectionCompanyLogo.SYDNEYMETRO,
    SectionCompanyLogo.BARRICK,
    SectionCompanyLogo.HAPAG_LLOYD,
    SectionCompanyLogo.PYBAR_MINING,
  ];
  lightOnly: SectionCompanyLogo[] = [
    SectionCompanyLogo.AUTOMATION_ANYWHERE,
    SectionCompanyLogo.UNIVERSITAT_BONN,
    SectionCompanyLogo.YARA,
  ];

  textColor: FontColor;

  async ngOnInit(): Promise<void> {
    const theme = await firstValueFrom(
      this.theme.getTheme().pipe(untilDestroyed(this)),
    );

    // Logo color should be the opposite of the current background.
    const logoColor =
      this.color === SectionCompanyLogoColor.DARK
        ? theme === 'dark'
          ? 'light'
          : 'dark'
        : this.color === SectionCompanyLogoColor.LIGHT
          ? 'light'
          : 'dark';

    // Decide which logo to use based on the logo color.
    // Some logos are pngs, some are svg and some only support light background.
    this.logoDetails = this.logos.map(logo => {
      let ext: 'png' | 'svg';
      let lColor: 'light' | 'dark';
      if (logo === SectionCompanyLogo.NASA && logoColor === 'light') {
        ext = 'png';
        lColor = 'light';
      } else if (this.lightOnly.includes(logo)) {
        ext = 'svg';
        lColor = 'dark';
      } else if (this.acceptedPNGLogos.includes(logo)) {
        ext = 'png';
        lColor = logoColor;
      } else {
        ext = 'svg';
        lColor = logoColor;
      }

      return {
        src: `company-logos/${logo}-${lColor}.${ext}`,
        alt: `${logo} logo`,
      };
    });

    this.cdr.markForCheck();
  }

  trackBySrc(_: number, item: PictureImage): string {
    return item.src;
  }
}
